import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { useToast, TOAST_TYPE, TOAST_PRIORITY } from '../../hooks/useToast';
import { SkeletonRow } from 'components/SkeletonLoader';
import { logError } from '../../utils/logError';


const FactionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faction, setFaction] = useState(null);
  const [myMembership, setMyMembership] = useState(null);
  const [members, setMembers] = useState([]);
  const [vaultLog, setVaultLog] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [allFactions, setAllFactions] = useState([]);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [txLoading, setTxLoading] = useState(false);
  const [txMsg, setTxMsg] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ faction_name: '', tag: '', description: '', banner_color: '#00D9FF' });
  const [createErrors, setCreateErrors] = useState({});
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [depositError, setDepositError] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const [playerCash, setPlayerCash] = useState(0);

  const fetchFactionData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: membership, error: memErr } = await supabase?.from('faction_members')?.select('*, factions(*)')?.eq('user_id', user?.id)?.maybeSingle();
      if (memErr) throw memErr;
      setMyMembership(membership);

      // Fetch player cash for validation
      const { data: statsData } = await supabase?.from('player_stats')?.select('cash')?.eq('user_id', user?.id)?.single();
      setPlayerCash(parseFloat(statsData?.cash || 0));

      if (membership?.factions) {
        setFaction(membership?.factions);
        const { data: membersData } = await supabase?.from('faction_members')?.select('*, user_profiles(username, account_status)')?.eq('faction_id', membership?.faction_id)?.order('rank', { ascending: true });
        setMembers(membersData || []);
        const { data: vaultData } = await supabase?.from('faction_vaults')?.select('*, user_profiles(username)')?.eq('faction_id', membership?.faction_id)?.order('created_at', { ascending: false })?.limit(20);
        setVaultLog(vaultData || []);
        const { data: terrData } = await supabase?.from('territory_ownership')?.select('*')?.eq('faction_id', membership?.faction_id);
        setTerritories(terrData || []);
      } else {
        const { data: factionsData } = await supabase?.from('factions')?.select('*')?.eq('is_active', true)?.order('faction_xp', { ascending: false })?.limit(20);
        setAllFactions(factionsData || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load faction data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchFactionData(); }, [fetchFactionData]);

  useRealtimeSubscription(
    `faction-updates-${myMembership?.faction_id}`,
    { table: 'factions', event: 'UPDATE', filter: myMembership?.faction_id ? `id=eq.${myMembership?.faction_id}` : undefined },
    useCallback((payload) => {
      if (payload?.new) setFaction(payload?.new);
    }, []),
    { enabled: !!myMembership?.faction_id }
  );

  const handleDeposit = async () => {
    const amt = parseFloat(depositAmount);
    setDepositError('');
    if (!depositAmount || isNaN(amt)) { setDepositError('Amount must be positive'); return; }
    if (amt <= 0) { setDepositError('Amount must be positive'); return; }
    if (amt > playerCash) { setDepositError(`Insufficient funds. You have $${playerCash?.toLocaleString()} available`); return; }

    // Optimistic UI: immediately update vault balance
    const prevFaction = { ...faction };
    const prevCash = playerCash;
    setFaction(prev => prev ? { ...prev, total_vault_balance: (parseFloat(prev?.total_vault_balance || 0) + amt) } : prev);
    setPlayerCash(prev => prev - amt);

    setTxLoading(true); setTxMsg(null);
    try {
      const { data, error } = await supabase?.rpc('deposit_vault', { p_amount: amt });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      setTxMsg({ type: 'success', text: `Deposited $${amt?.toLocaleString()} to vault. ${data?.message || ''}` });
      showToast(
        `Faction vault deposit: $${amt?.toLocaleString()}`,
        TOAST_TYPE?.FACTION_DEPOSIT,
        TOAST_PRIORITY?.NORMAL
      );
      setDepositAmount('');
      fetchFactionData();
    } catch (err) {
      // Rollback
      setFaction(prevFaction);
      setPlayerCash(prevCash);
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('insufficient') || msg?.includes('funds')) {
        setDepositError('Insufficient funds for this deposit.');
      } else {
        setTxMsg({ type: 'error', text: `Transaction failed — vault balance restored. ${err?.message}` });
      }
      logError({ error: err, componentName: 'Factions', actionName: 'handleDeposit', screenName: 'factions', userId: user?.id, context: { amount: amt } });
    } finally {
      setTxLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    setWithdrawError('');
    const vaultBalance = parseFloat(faction?.total_vault_balance || 0);
    if (!withdrawAmount || isNaN(amt)) { setWithdrawError('Amount must be positive'); return; }
    if (amt <= 0) { setWithdrawError('Amount must be positive'); return; }
    if (amt > vaultBalance) { setWithdrawError(`Cannot withdraw more than vault balance ($${vaultBalance?.toLocaleString()})`); return; }
    const isLeaderOrOfficer = ['LEADER', 'OFFICER']?.includes(myMembership?.rank);
    if (!isLeaderOrOfficer) { setWithdrawError('Only Leaders and Officers can withdraw from the vault'); return; }

    // Optimistic UI: immediately update vault balance
    const prevFaction = { ...faction };
    const prevCash = playerCash;
    setFaction(prev => prev ? { ...prev, total_vault_balance: (parseFloat(prev?.total_vault_balance || 0) - amt) } : prev);
    setPlayerCash(prev => prev + amt);

    setTxLoading(true); setTxMsg(null);
    try {
      const { data, error } = await supabase?.rpc('withdraw_vault', { p_amount: amt });
      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'Withdrawal failed');
      }
      setTxMsg({ type: 'success', text: `Withdrew $${amt?.toLocaleString()} from vault.` });
      setWithdrawAmount('');
      fetchFactionData();
    } catch (err) {
      // Rollback
      setFaction(prevFaction);
      setPlayerCash(prevCash);
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('permission') || msg?.includes('rank')) {
        setWithdrawError('Permission denied. You do not have sufficient rank for this withdrawal.');
      } else if (msg?.includes('insufficient') || msg?.includes('funds')) {
        setWithdrawError('Insufficient vault funds.');
      } else {
        setTxMsg({ type: 'error', text: `Transaction failed — vault balance restored. ${err?.message || 'Withdrawal failed'}` });
      }
      logError({ error: err, componentName: 'Factions', actionName: 'handleWithdraw', screenName: 'factions', userId: user?.id, context: { amount: amt } });
    } finally {
      setTxLoading(false);
    }
  };

  const validateCreateForm = () => {
    const errs = {};
    if (!createForm?.faction_name?.trim()) {
      errs.faction_name = 'Faction name is required';
    } else if (createForm?.faction_name?.trim()?.length < 3) {
      errs.faction_name = 'Name must be at least 3 characters';
    } else if (createForm?.faction_name?.trim()?.length > 50) {
      errs.faction_name = 'Name must be 50 characters or less';
    }
    if (!createForm?.tag?.trim()) {
      errs.tag = 'Tag is required';
    } else if (createForm?.tag?.trim()?.length < 2) {
      errs.tag = 'Tag must be at least 2 characters';
    } else if (createForm?.tag?.trim()?.length > 6) {
      errs.tag = 'Tag must be 6 characters or less';
    }
    return errs;
  };

  const handleCreateFaction = async () => {
    const errs = validateCreateForm();
    if (Object.keys(errs)?.length > 0) { setCreateErrors(errs); return; }
    if (playerCash < 50000) {
      setCreateError(`Insufficient funds. Creating a faction costs $50,000. You have $${playerCash?.toLocaleString()}.`);
      return;
    }
    setCreateLoading(true); setCreateError(null); setCreateErrors({});
    try {
      const { data, error } = await supabase?.rpc('create_faction', {
        p_faction_name: createForm?.faction_name?.trim(),
        p_tag: createForm?.tag?.trim()?.toUpperCase(),
        p_description: createForm?.description,
        p_banner_color: createForm?.banner_color,
      });
      if (error) throw error;
      if (!data?.success) {
        const msg = data?.error?.toLowerCase() || '';
        if (msg?.includes('name') && (msg?.includes('taken') || msg?.includes('exists') || msg?.includes('duplicate'))) {
          setCreateErrors({ faction_name: 'This faction name is already taken. Choose a different name.' });
        } else if (msg?.includes('funds') || msg?.includes('cash') || msg?.includes('insufficient')) {
          setCreateError(`Insufficient funds. Creating a faction costs $50,000.`);
        } else if (msg?.includes('already') && msg?.includes('faction')) {
          setCreateError('You are already in a faction. Leave your current faction first.');
        } else {
          setCreateError(data?.error || 'Failed to create faction.');
        }
        return;
      }
      setShowCreateModal(false);
      setCreateForm({ faction_name: '', tag: '', description: '', banner_color: '#00D9FF' });
      fetchFactionData();
    } catch (err) {
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('duplicate') || msg?.includes('unique') || msg?.includes('already exists')) {
        setCreateErrors({ faction_name: 'This faction name is already taken.' });
      } else if (msg?.includes('funds') || msg?.includes('insufficient')) {
        setCreateError('Insufficient funds. Creating a faction costs $50,000.');
      } else {
        setCreateError(err?.message || 'Failed to create faction.');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const handleJoinFaction = async (factionId) => {
    try {
      const { data, error } = await supabase?.rpc('join_faction', { p_faction_id: factionId });
      if (error) throw error;
      if (!data?.success) {
        const msg = data?.error?.toLowerCase() || '';
        if (msg?.includes('already') && msg?.includes('faction')) {
          setError('You are already in a faction. Leave your current faction before joining another.');
        } else if (msg?.includes('full') || msg?.includes('max_members')) {
          setError('This faction is full. Try a different one.');
        } else {
          setError(data?.error || 'Failed to join faction.');
        }
        return;
      }
      fetchFactionData();
    } catch (err) {
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('already') && msg?.includes('faction')) {
        setError('You are already in a faction.');
      } else {
        setError(err?.message || 'Failed to join faction.');
      }
    }
  };

  const handleLeaveFaction = async () => {
    if (myMembership?.rank === 'LEADER') {
      if (!window.confirm('You are the faction leader. Leaving will disband the faction or transfer leadership. Are you sure?')) return;
    } else {
      if (!window.confirm('Are you sure you want to leave this faction?')) return;
    }
    try {
      const { data, error } = await supabase?.rpc('leave_faction');
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      setFaction(null); setMyMembership(null);
      fetchFactionData();
    } catch (err) {
      setError(err?.message || 'Failed to leave faction.');
    }
  };

  const rankColor = (rank) => {
    const colors = { LEADER: 'text-yellow-400', OFFICER: 'text-primary', MEMBER: 'text-foreground', RECRUIT: 'text-muted-foreground' };
    return colors?.[rank] || 'text-muted-foreground';
  };

  const txTypeColor = (type) => {
    const colors = { DEPOSIT: 'text-success', WITHDRAW: 'text-red-400', WAR_COST: 'text-red-400', WAR_REWARD: 'text-yellow-400', TERRITORY_BONUS: 'text-primary' };
    return colors?.[type] || 'text-foreground';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'members', label: 'Members', icon: 'Users' },
    { id: 'vault', label: 'Vault', icon: 'Vault' },
    { id: 'territory', label: 'Territory', icon: 'Map' },
  ];

  if (loading) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">Loading faction data...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!faction) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-h2 font-heading text-foreground flex items-center gap-3">
                  <Icon name="Users" size={28} color="var(--color-primary)" />
                  Factions
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Join or create a faction to build your empire</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-zinc-950 rounded-md text-sm font-semibold hover:bg-primary/90"
              >
                <Icon name="Plus" size={14} />
                Create Faction
              </button>
            </div>

            {error && (
              <div className="card p-4 border-red-500/30 mb-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-3">
              {allFactions?.length === 0 ? (
                <div className="card p-8 text-center">
                  <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No factions yet. Be the first to create one!</p>
                </div>
              ) : (
                allFactions?.map(f => (
                  <div key={f?.id} className="card p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-sm font-bold font-mono"
                        style={{ background: (f?.banner_color || '#00D9FF') + '20', border: `2px solid ${f?.banner_color || '#00D9FF'}40`, color: f?.banner_color || '#00D9FF' }}
                      >
                        [{f?.tag}]
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{f?.faction_name}</p>
                        <p className="text-xs text-muted-foreground">{f?.description || 'No description'}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">Level {f?.level}</span>
                          <span className="text-xs text-primary">{Number(f?.faction_xp || 0)?.toLocaleString()} XP</span>
                          <span className="text-xs text-success">${Number(f?.total_vault_balance || 0)?.toLocaleString()} vault</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleJoinFaction(f?.id)}
                      className="px-4 py-2 bg-primary/10 text-primary border border-primary/30 rounded-md text-sm font-semibold hover:bg-primary/20"
                    >
                      Join
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </main>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="card p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-bold text-foreground">Create Faction</h2>
                <button onClick={() => { setShowCreateModal(false); setCreateErrors({}); setCreateError(null); }} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={18} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-1">Creation cost: <span className="text-warning font-mono">$50,000</span></p>
              <p className="text-xs mb-4" style={{ color: playerCash >= 50000 ? 'var(--color-success)' : 'var(--color-error)' }}>
                Your cash: ${playerCash?.toLocaleString()}
              </p>
              {createError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/30 mb-3">
                  <Icon name="AlertCircle" size={14} color="var(--color-error)" className="mt-0.5 shrink-0" />
                  <p className="text-xs text-error">{createError}</p>
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Faction Name <span className="text-error">*</span></label>
                  <input
                    value={createForm?.faction_name}
                    onChange={e => { setCreateForm(p => ({ ...p, faction_name: e?.target?.value })); if (createErrors?.faction_name) setCreateErrors(p => ({ ...p, faction_name: '' })); }}
                    placeholder="Iron Syndicate (3-50 chars)"
                    maxLength={50}
                    className={`w-full bg-surface border rounded-md px-3 py-2 text-sm text-foreground ${
                      createErrors?.faction_name ? 'border-error' : 'border-border'
                    }`}
                  />
                  {createErrors?.faction_name && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={11} />{createErrors?.faction_name}</p>}
                  <p className="mt-0.5 text-xs text-muted-foreground">{createForm?.faction_name?.length}/50</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Tag (2-6 chars) <span className="text-error">*</span></label>
                  <input
                    value={createForm?.tag}
                    onChange={e => { setCreateForm(p => ({ ...p, tag: e?.target?.value?.toUpperCase() })); if (createErrors?.tag) setCreateErrors(p => ({ ...p, tag: '' })); }}
                    placeholder="IRON"
                    maxLength={6}
                    className={`w-full bg-surface border rounded-md px-3 py-2 text-sm text-foreground font-mono ${
                      createErrors?.tag ? 'border-error' : 'border-border'
                    }`}
                  />
                  {createErrors?.tag && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={11} />{createErrors?.tag}</p>}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Description</label>
                  <textarea
                    value={createForm?.description}
                    onChange={e => setCreateForm(p => ({ ...p, description: e?.target?.value }))}
                    placeholder="Describe your faction..."
                    rows={2}
                    className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-foreground resize-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Banner Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={createForm?.banner_color}
                      onChange={e => setCreateForm(p => ({ ...p, banner_color: e?.target?.value }))}
                      className="w-10 h-10 rounded cursor-pointer bg-surface border border-border"
                    />
                    <span className="text-sm font-mono text-muted-foreground">{createForm?.banner_color}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => { setShowCreateModal(false); setCreateErrors({}); setCreateError(null); }}
                  className="flex-1 py-2 bg-surface border border-border rounded-md text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFaction}
                  disabled={createLoading || playerCash < 50000}
                  className="flex-1 py-2 bg-primary text-zinc-950 rounded-md text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createLoading ? 'Creating...' : playerCash < 50000 ? 'Insufficient Funds' : 'Create Faction'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">
          {error && (
            <div className="card p-4 border-red-500/30 mb-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="card p-6 mb-6" style={{ borderColor: (faction?.banner_color || '#00D9FF') + '40' }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-lg flex items-center justify-center text-sm font-bold font-mono"
                  style={{ background: (faction?.banner_color || '#00D9FF') + '20', border: `2px solid ${faction?.banner_color || '#00D9FF'}40`, color: faction?.banner_color || '#00D9FF' }}
                >
                  [{faction?.tag}]
                </div>
                <div>
                  <h1 className="text-h2 font-heading text-foreground">{faction?.faction_name}</h1>
                  <p className="text-sm text-muted-foreground">{faction?.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded bg-primary/10 border border-primary/20 font-semibold ${rankColor(myMembership?.rank)}`}>
                      {myMembership?.rank}
                    </span>
                    <span className="text-xs text-muted-foreground">Level {faction?.level} Faction</span>
                    <button
                      onClick={handleLeaveFaction}
                      className="text-xs text-red-400 hover:text-red-300 underline"
                    >
                      Leave
                    </button>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Faction XP</p>
                <p className="text-xl font-mono font-bold text-primary">{Number(faction?.faction_xp || 0)?.toLocaleString()}</p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t border-border">
              {[
                { label: 'Members', value: `${members?.length}/${faction?.max_members}`, icon: 'Users' },
                { label: 'Vault', value: `$${(Number(faction?.total_vault_balance || 0) / 1000)?.toFixed(0)}K`, icon: 'Vault' },
                { label: 'Territories', value: territories?.length, icon: 'Map' },
                { label: 'Level', value: faction?.level, icon: 'Star' },
              ]?.map(stat => (
                <div key={stat?.label} className="text-center">
                  <Icon name={stat?.icon} size={16} color="var(--color-muted-foreground)" className="mx-auto mb-1" />
                  <p className="text-lg font-mono font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-1 mb-6 bg-surface rounded-lg p-1">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab?.id ? 'bg-primary text-zinc-950' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={14} />
                {tab?.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Rank Hierarchy</h3>
                <div className="space-y-2">
                  {['LEADER', 'OFFICER', 'MEMBER', 'RECRUIT']?.map((rank, i) => (
                    <div key={rank} className={`flex items-center justify-between p-2 rounded ${
                      rank === myMembership?.rank ? 'bg-primary/10 border border-primary/20' : 'bg-surface'
                    }`}>
                      <span className={`text-sm font-medium ${rankColor(rank)}`}>{rank}</span>
                      <span className="text-xs text-muted-foreground">Tier {4 - i}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {[
                    { label: 'Deposit to Vault', icon: 'ArrowUpCircle', color: 'text-success', action: () => setActiveTab('vault') },
                    { label: 'Declare War', icon: 'Swords', color: 'text-red-400', action: () => navigate('/wars') },
                    { label: 'View Territory', icon: 'Map', color: 'text-primary', action: () => setActiveTab('territory') },
                    { label: 'View Leaderboard', icon: 'Trophy', color: 'text-warning', action: () => navigate('/leaderboards') },
                  ]?.map(action => (
                    <button
                      key={action?.label}
                      onClick={action?.action}
                      className="w-full flex items-center gap-3 p-3 rounded-md bg-surface hover:bg-surface/80 border border-border hover:border-primary/30 transition-all text-left"
                    >
                      <Icon name={action?.icon} size={16} className={action?.color} />
                      <span className="text-sm text-foreground">{action?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Members ({members?.length}/{faction?.max_members})</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase">Player</th>
                    <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase">Rank</th>
                    <th className="text-right p-4 text-xs font-semibold text-muted-foreground uppercase">Contribution</th>
                    <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {members?.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-8 text-center">
                        <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No members yet</p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {members?.map(member => (
                        <tr key={member?.id} className="border-b border-border/50 hover:bg-surface/50 transition-colors">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                                <span className="text-xs font-bold text-primary">{member?.user_profiles?.username?.[0]?.toUpperCase()}</span>
                              </div>
                              <span className="text-sm font-medium text-foreground">{member?.user_profiles?.username}</span>
                              {member?.user_id === user?.id && <span className="text-xs text-primary">(you)</span>}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-0.5 rounded bg-surface border border-border font-semibold ${rankColor(member?.rank)}`}>
                              {member?.rank}
                            </span>
                          </td>
                          <td className="p-4 text-right font-mono text-sm text-foreground">${Number(member?.contribution || 0)?.toLocaleString()}</td>
                          <td className="p-4 text-xs text-muted-foreground">{new Date(member?.joined_at)?.toLocaleDateString()}</td>
                        </tr>
                      ))}
                      <SkeletonRow count={5} />
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'vault' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Vault Balance</h3>
                <p className="text-4xl font-mono font-bold text-success mb-1">${Number(faction?.total_vault_balance || 0)?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mb-4">Vault funds cannot be used as margin collateral</p>

                {txMsg && (
                  <div className={`p-3 rounded-md mb-3 text-sm ${txMsg?.type === 'success' ? 'bg-success/10 text-success border border-success/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {txMsg?.text}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Deposit Amount</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Amount"
                        value={depositAmount}
                        onChange={e => { setDepositAmount(e?.target?.value); setDepositError(''); }}
                        min={1}
                        max={playerCash}
                        className={`flex-1 bg-surface border rounded-md px-3 py-2 text-sm text-foreground ${
                          depositError ? 'border-error' : 'border-border'
                        }`}
                      />
                      <button
                        onClick={handleDeposit}
                        disabled={txLoading || !depositAmount}
                        className="px-4 py-2 bg-success text-white rounded-md text-sm font-semibold hover:bg-success/90 disabled:opacity-50"
                      >
                        {txLoading ? '...' : 'Deposit'}
                      </button>
                    </div>
                    {depositError && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={11} />{depositError}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">Available: <span className="text-success font-mono">${playerCash?.toLocaleString()}</span></p>
                  </div>

                  {['LEADER', 'OFFICER']?.includes(myMembership?.rank) && (
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Withdraw Amount (Officer+)</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          placeholder="Amount"
                          value={withdrawAmount}
                          onChange={e => { setWithdrawAmount(e?.target?.value); setWithdrawError(''); }}
                          min={1}
                          max={parseFloat(faction?.total_vault_balance || 0)}
                          className={`flex-1 bg-surface border rounded-md px-3 py-2 text-sm text-foreground ${
                            withdrawError ? 'border-error' : 'border-border'
                          }`}
                        />
                        <button
                          onClick={handleWithdraw}
                          disabled={txLoading || !withdrawAmount}
                          className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md text-sm font-semibold hover:bg-red-500/20 disabled:opacity-50"
                        >
                          {txLoading ? '...' : 'Withdraw'}
                        </button>
                      </div>
                      {withdrawError && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={11} />{withdrawError}</p>}
                      <p className="mt-1 text-xs text-muted-foreground">Vault: <span className="text-success font-mono">${parseFloat(faction?.total_vault_balance || 0)?.toLocaleString()}</span></p>
                    </div>
                  )}

                  {!['LEADER', 'OFFICER']?.includes(myMembership?.rank) && (
                    <div className="p-3 rounded-lg bg-surface border border-border">
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Icon name="Lock" size={12} color="var(--color-muted-foreground)" />
                        Withdrawals require Officer or Leader rank
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="card p-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Vault Audit Log</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {vaultLog?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No transactions yet</p>
                  ) : (
                    vaultLog?.map(entry => (
                      <div key={entry?.id} className="flex items-center justify-between text-sm py-2 border-b border-border/30">
                        <div>
                          <span className={`font-semibold ${txTypeColor(entry?.transaction_type)}`}>{entry?.transaction_type}</span>
                          <span className="text-muted-foreground ml-2 text-xs">by {entry?.user_profiles?.username}</span>
                        </div>
                        <div className="text-right">
                          <span className={`font-mono text-xs ${entry?.amount > 0 ? 'text-success' : 'text-red-400'}`}>
                            {entry?.amount > 0 ? '+' : ''}${Number(entry?.amount)?.toLocaleString()}
                          </span>
                          <p className="text-xs text-muted-foreground">{new Date(entry?.created_at)?.toLocaleTimeString()}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'territory' && (
            <div className="space-y-3">
              {territories?.length === 0 ? (
                <div className="card p-8 text-center">
                  <Icon name="Map" size={40} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No territories controlled. Win wars to claim territory!</p>
                </div>
              ) : (
                territories?.map(t => (
                  <div key={t?.id} className="card p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <Icon name="Map" size={18} color="var(--color-primary)" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{t?.territory_name}</p>
                        <p className="text-xs text-success">{t?.bonus_description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono font-bold text-primary">{t?.control_percentage}%</p>
                      <p className="text-xs text-muted-foreground">Control</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FactionsPage;
